import {CheckSpinner} from '@github-ui/mergebox'
import {RelativeTime} from '@primer/react'
import {
  CheckCircleFillIcon,
  GitMergeIcon,
  StopIcon,
  SkipIcon,
  GitMergeQueueIcon,
  GitPullRequestClosedIcon,
  DotFillIcon,
  ClockIcon,
} from '@primer/octicons-react'
import styles from './TaskListItem.module.css'
import {useMemo, useState, useEffect} from 'react'
import {getReadableSessionState, type SessionState} from '../types/session'
import {ListBlock} from '@github-ui/pacer/ListBlock'
import {Icon} from '@github-ui/pacer/Icon'
import {WithAnimatedEllipsis} from '@github-ui/copilot-chat/components/WithAnimatedEllipsis'

export interface TaskListItemProps {
  state?: SessionState
  startedAt?: string
  revisionCount: number
  prState: string
  prInMergeQueue: boolean
  prUrl: string
  prTitle: string
  prNwo: string
  prNumber: number
}

export interface OptimisticTaskListItemProps {
  title: string
  repositoryNwo: string
}

interface IconAttributes {
  icon: React.ComponentType | (() => React.ReactElement)
  color: string
  'data-testid': string
}

const FadeText = ({text}: {text: string}) => {
  const [displayedText, setDisplayedText] = useState<string | null>(null)
  const [animationState, setAnimationState] = useState('none') // 'entering' | 'exiting' | 'none'

  useEffect(() => {
    if (displayedText === null) {
      // First render - set text immediately without animation
      setDisplayedText(text)
      return
    }

    if (text !== displayedText) {
      setAnimationState('exiting')
      const timeout = setTimeout(() => {
        setDisplayedText(text)
        setAnimationState('entering')
      }, 400)
      return () => clearTimeout(timeout)
    }
  }, [text, displayedText])

  return (
    <span
      className={`${styles.fadeText} ${
        animationState === 'entering' ? styles.entering : animationState === 'exiting' ? styles.exiting : null
      }`}
    >
      {displayedText}
    </span>
  )
}

export function getLeadingVisualIconAttributes(
  prState: string,
  prInMergeQueue: boolean,
  state?: SessionState,
): IconAttributes {
  // check if the pull request is open and has a state
  if (prInMergeQueue) {
    return {
      icon: GitMergeQueueIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-git-merge-queue',
    }
  } else if (state === 'queued') {
    return {
      icon: ClockIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-clock',
    }
  } else if (prState === 'open') {
    if (state === 'waiting_for_user' || state === 'completed') {
      return {
        icon: CheckCircleFillIcon,
        color: 'var(--fgColor-success)' as const,
        'data-testid': 'icon-check-circle-fill',
      }
    }
    if (state === 'failed' || state === 'timed_out') {
      return {
        icon: StopIcon,
        color: 'var(--fgColor-danger)' as const,
        'data-testid': 'icon-stop',
      }
    }
    if (state === 'in_progress') {
      return {
        icon: () => <CheckSpinner animated />,
        color: 'attention.fg' as const,
        'data-testid': 'icon-spinning-circle',
      }
    }

    if (state === 'idle') {
      return {
        icon: DotFillIcon,
        color: 'var(--fgColor-muted)' as const,
        'data-testid': 'icon-dot',
      }
    }

    if (state === 'cancelled') {
      return {
        icon: SkipIcon,
        color: 'var(--fgColor-muted)' as const,
        'data-testid': 'icon-skip',
      }
    }
  }

  // check if the pull request is merged
  if (prState === 'merged') {
    return {
      icon: GitMergeIcon,
      color: 'var(--fgColor-done)' as const,
      'data-testid': 'icon-git-merge',
    }
  }

  if (prState === 'closed') {
    return {
      icon: GitPullRequestClosedIcon,
      color: 'var(--fgColor-danger)' as const,
      'data-testid': 'icon-skip',
    }
  }

  return {
    icon: DotFillIcon,
    color: 'var(--fgColor-muted)' as const,
    'data-testid': 'icon-idle',
  }
}

export function TaskListItem({
  state,
  revisionCount,
  startedAt,
  prState,
  prUrl,
  prTitle,
  prNwo,
  prNumber,
  prInMergeQueue,
}: TaskListItemProps) {
  // State for managing icon transitions
  const [displayedIconAttributes, setDisplayedIconAttributes] = useState<IconAttributes | null>(null)
  const [isIconTransitioning, setIsIconTransitioning] = useState(false)

  // Calculate the current log text
  const currentLogText = useMemo(() => {
    return prState === 'merged'
      ? 'Merged'
      : prState === 'closed'
        ? 'Closed'
        : prInMergeQueue
          ? 'Queued to merge'
          : (state && getReadableSessionState(state)) || ''
  }, [prInMergeQueue, prState, state])

  const leadingVisualIconAttributes = useMemo(
    () => getLeadingVisualIconAttributes(prState, prInMergeQueue, state),
    [prState, prInMergeQueue, state],
  )
  // Handle icon transitions when the icon changes
  useEffect(() => {
    if (!displayedIconAttributes) {
      // Initial mount - no animation
      setDisplayedIconAttributes(leadingVisualIconAttributes)
      return
    }

    // Compare the current icon with displayed icon to detect changes
    const hasIconChanged = displayedIconAttributes.icon !== leadingVisualIconAttributes.icon

    if (hasIconChanged) {
      // Icon is changing - start transition
      setIsIconTransitioning(true)

      // Fade out old icon, then fade in new icon
      const fadeOutTimer = setTimeout(() => {
        setDisplayedIconAttributes(leadingVisualIconAttributes)
        setIsIconTransitioning(false)
      }, 300)

      return () => clearTimeout(fadeOutTimer)
    }
  }, [leadingVisualIconAttributes, displayedIconAttributes])

  const revisionTextWithTrailingSeparator =
    revisionCount > 0 ? `${revisionCount} revision${revisionCount > 1 ? 's' : ''} · ` : null

  return (
    <ListBlock.Item href={prUrl} isExternalLink>
      <ListBlock.Item.LeadingVisual>
        <Icon
          {...leadingVisualIconAttributes}
          className={`${styles.iconTransition} ${isIconTransitioning ? styles.fadeOut : ''}`}
        />
      </ListBlock.Item.LeadingVisual>
      <ListBlock.Item.Title>{prTitle}</ListBlock.Item.Title>
      <ListBlock.Item.Description>
        <span className={styles.description}>
          {prNwo}#{prNumber} · started <RelativeTime format="micro" datetime={startedAt} /> ago ·{' '}
          {revisionTextWithTrailingSeparator}
          <FadeText text={currentLogText} />
        </span>
      </ListBlock.Item.Description>
    </ListBlock.Item>
  )
}

export function LoadingTaskListItem() {
  return <ListBlock.Item.Skeleton />
}

// TODO: Put inside TaskListItem with a prop, so we can get transition functionality included
export function OptimisticTaskListItem({repositoryNwo}: OptimisticTaskListItemProps) {
  return (
    <ListBlock.Item aria-label={`Creating task in ${repositoryNwo}`}>
      <ListBlock.Item.LeadingVisual>
        <Icon icon={DotFillIcon} color={'var(--fgColor-attention)' as const} />
      </ListBlock.Item.LeadingVisual>
      <ListBlock.Item.Title>
        <WithAnimatedEllipsis>Creating task</WithAnimatedEllipsis>
      </ListBlock.Item.Title>
      <ListBlock.Item.Description>
        <span className={styles.description}>{repositoryNwo}</span> · Initializing pull request…
      </ListBlock.Item.Description>
    </ListBlock.Item>
  )
}

try{ FadeText.displayName ||= 'FadeText' } catch {}
try{ TaskListItem.displayName ||= 'TaskListItem' } catch {}
try{ LoadingTaskListItem.displayName ||= 'LoadingTaskListItem' } catch {}
try{ OptimisticTaskListItem.displayName ||= 'OptimisticTaskListItem' } catch {}